/*
 * The shell: the menus, the phone sheet, the bag badge, and the two bars at the
 * bottom of a phone.
 *
 * EVERYTHING HERE IS AN UPGRADE, NOT A FEATURE
 * --------------------------------------------
 * Every destination behind a dropdown is also a plain link on `/schools`,
 * `/category/<slug>` or `/search`; every destination behind the ☰ sheet is also in
 * `templates/includes/tab_bar.html`, which is five ordinary anchors that never need
 * a script (see that file's own note). So when this module does nothing at all, the
 * storefront is still navigable — which is the whole reason the templates were
 * written the way they were.
 *
 * WHAT THIS MODULE OWNS THAT NOTHING ELSE CAN
 * -------------------------------------------
 * Two facts about a visitor live only in their browser and can never be
 * server-rendered:
 *
 *   * **The bag.** Frappe hands every signed-out visitor the SAME "Guest" session
 *     (frappe/sessions.py:391), so a bag kept in the session would be handed to the
 *     next stranger. The bag therefore travels in the URL (`www/bag.py`), and the
 *     only thing that can carry it from page to page is this browser's own storage.
 *     That is why the badge, the Bag links and "add to bag" all live in one place.
 *   * **The last list they opened.** `/` is rendered identically for everyone, so
 *     the "your last list" row is printed by the server with the honest thing it
 *     knows (every class at the featured school) and rewritten here from memory.
 *
 * THE ONE CONTRACT THIS MODULE HAS WITH THE STYLESHEET
 * ---------------------------------------------------
 * An open dropdown is `.sb-mi.sb-open`, an open sheet is `.sb-sheet.sb-open`, and
 * the class is the mockup's own `.open` with the section-16 prefix on it. `hidden`
 * is toggled alongside on the sheet so the panel still opens on a stylesheet that
 * chose to key on the attribute instead. Nothing here reads a class it did not put
 * there.
 *
 * NO SERVER STRING IS EVER WRITTEN AS MARKUP. School names, item names and the
 * remembered list's own title all go in through `textContent`, and the two nodes
 * this module creates are built with `document.createElement`
 * (BUILD_DECISIONS section 10).
 */

import { ApiError, METHODS, post } from "./api.js";
import { bag as bagStore, lastList, theme as themeStore } from "./storage.js";

const OPEN = "sb-open";

/** Every element that carries a bag count: the header pill, the phone tab bar. */
const BADGE = "[data-sb-bag-count]";

/* ── small helpers ─────────────────────────────────────────────────────────── */
/*
 * `set.js`, `otp.js` and `upi.js` import these rather than each keeping a copy.
 * They live in this module because it is the one every other module already depends
 * on (for the bag badge), and because the bundle's file list is fixed — a seventh
 * file for six functions would buy nothing.
 */

/** Every match, as a real array. */
export function all(selector, root) {
	return Array.prototype.slice.call((root || document).querySelectorAll(selector));
}

/** The first match, or null. */
export function one(selector, root) {
	return (root || document).querySelector(selector);
}

/** Remove every child of a node, so it can be repainted. */
export function empty(node) {
	while (node && node.firstChild) node.removeChild(node.firstChild);
}

/**
 * A `<span>`, optionally classed and filled.
 *
 * `textContent`, always: item names, school names and the shop's own copy are typed
 * by people and every one of them is a raw-HTML sink if it goes anywhere near
 * `innerHTML` (docs/BUILD_DECISIONS.md section 10 — the JavaScript half of the same
 * rule the templates follow with `| e`).
 */
export function span(className, text) {
	const node = document.createElement("span");
	if (className) node.className = className;
	if (text !== undefined && text !== null) node.textContent = String(text);
	return node;
}

/**
 * `<svg class="sb-ic" aria-hidden="true"><use href="#sb-i-info"></use></svg>` — the
 * same element `splashbox.utils.icons.icon()` prints.
 *
 * Built in the SVG namespace because a `document.createElement("svg")` is an unknown
 * HTML element that draws nothing at all. `name` is only ever a literal from this
 * bundle, never a value from the server.
 */
export function icon(name) {
	const NS = "http://www.w3.org/2000/svg";
	const svg = document.createElementNS(NS, "svg");
	svg.setAttribute("class", "sb-ic");
	svg.setAttribute("aria-hidden", "true");
	svg.setAttribute("focusable", "false");
	const use = document.createElementNS(NS, "use");
	use.setAttribute("href", "#sb-i-" + name);
	svg.appendChild(use);
	return svg;
}

/** Lower-cased, collapsed text of a node — the needle and the haystack both. */
function needle(text) {
	return String(text || "")
		.toLowerCase()
		.replace(/\s+/g, " ")
		.trim();
}

/**
 * A same-site path we are willing to navigate to.
 *
 * The same rule as `splashbox.api.auth._safe_redirect` and `www/bag.py:_safe_path`:
 * one leading separator and no second one, because a browser normalises
 * "/\\evil.example" to "//evil.example" and leaves the site. Applied to every value
 * that reaches `location.assign` or an `href`, wherever it came from.
 */
export function safePath(raw) {
	const path = typeof raw === "string" ? raw.trim() : "";
	if (!path || path.charAt(0) !== "/") return "";
	const second = path.charAt(1);
	return second === "/" || second === "\\" ? "" : path;
}

/* ── the desktop dropdowns ─────────────────────────────────────────────────── */

/**
 * Click-to-open on top of the stylesheet's hover-to-open.
 *
 * The menu bar is only ever shown on a pointer device, and `menubar.html` already
 * opens each panel on hover under `@media (hover: hover)`. What hover cannot serve
 * is a keyboard (there is no hover), a touch laptop (a tap is a hover that never
 * ends) and a screen reader (which needs `aria-expanded` to change). So this adds
 * click, Escape, and the aria state — and deliberately does NOT add its own hover
 * handling, because two things racing to open the same panel is how a menu starts
 * flickering under the cursor.
 */
function menus() {
	const items = all("[data-sb-menu]");
	if (!items.length) return;

	function close(item) {
		item.classList.remove(OPEN);
		const button = one("button", item);
		if (button) button.setAttribute("aria-expanded", "false");
	}

	function closeAll(except) {
		items.forEach(function (item) {
			if (item !== except) close(item);
		});
	}

	items.forEach(function (item) {
		const button = one("button", item);
		if (!button) return;

		button.addEventListener("click", function (event) {
			event.preventDefault();
			const wasOpen = item.classList.contains(OPEN);
			closeAll(item);
			if (wasOpen) {
				close(item);
			} else {
				item.classList.add(OPEN);
				button.setAttribute("aria-expanded", "true");
			}
		});

		// A panel the customer has tabbed out of is closed, so the next Tab does not
		// land on a link that is visually behind the page.
		item.addEventListener("focusout", function (event) {
			if (!item.contains(event.relatedTarget)) close(item);
		});
	});

	document.addEventListener("click", function (event) {
		if (!event.target.closest("[data-sb-menu]")) closeAll(null);
	});

	document.addEventListener("keydown", function (event) {
		if (event.key !== "Escape") return;
		const open = items.filter(function (item) {
			return item.classList.contains(OPEN);
		});
		if (!open.length) return;
		open.forEach(close);
		const button = one("button", open[0]);
		if (button) button.focus();
	});
}

/* ── the phone slide-in sheet ──────────────────────────────────────────────── */

/**
 * The ☰ panel. It is the ONLY thing on this storefront that cannot be reached
 * without JavaScript, which `mobile_sheet.html` says out loud and which is safe
 * precisely because every destination inside it is also a tab or a footer link.
 *
 * Two attributes are toggled, not one: `hidden` (which the template ships, so the
 * panel is closed before this module runs and stays closed if it never does) and the
 * `sb-open` class the stylesheet animates on.
 */
function sheet() {
	const panel = one("[data-sb-sheet]");
	if (!panel) return;

	const openers = all("[data-sb-open-menu]");
	const closers = all("[data-sb-close-menu]");
	if (!openers.length) return;

	let restoreFocusTo = null;
	let previousOverflow = "";

	function open() {
		restoreFocusTo = document.activeElement;
		panel.removeAttribute("hidden");
		panel.classList.add(OPEN);
		previousOverflow = document.body.style.overflow;
		// A sheet that scrolls the page behind it reads as a broken page, and on a
		// phone the customer cannot see that the page moved until they close it.
		document.body.style.overflow = "hidden";
		const dialog = one(".sb-sheet-panel", panel) || panel;
		if (!dialog.hasAttribute("tabindex")) dialog.setAttribute("tabindex", "-1");
		dialog.focus();
	}

	function close() {
		panel.classList.remove(OPEN);
		panel.setAttribute("hidden", "hidden");
		document.body.style.overflow = previousOverflow;
		if (restoreFocusTo && typeof restoreFocusTo.focus === "function") restoreFocusTo.focus();
		restoreFocusTo = null;
	}

	openers.forEach(function (button) {
		button.addEventListener("click", function (event) {
			event.preventDefault();
			open();
		});
	});

	closers.forEach(function (button) {
		button.addEventListener("click", function (event) {
			event.preventDefault();
			close();
		});
	});

	// The scrim. `panel` is the full-screen backdrop and `.sb-sheet-panel` is the
	// card inside it, so a click that landed on the backdrop itself is a click
	// outside the menu.
	panel.addEventListener("click", function (event) {
		if (event.target === panel) close();
	});

	// Following a link inside the sheet navigates away; leaving the sheet open
	// across the load makes the next page flash a menu it never asked for.
	panel.addEventListener("click", function (event) {
		if (event.target.closest("a[href]")) close();
	});

	document.addEventListener("keydown", function (event) {
		if (event.key === "Escape" && panel.classList.contains(OPEN)) close();
	});
}

/* ── the two bars at the bottom of a phone ─────────────────────────────────── */

/**
 * Two stacked bars at the bottom of a 360px screen is one bar too many.
 *
 * The server already decides this — a page sets `bottom_bar = "total"` and
 * `tab_bar.html` steps aside — so this is the belt to that braces: it publishes the
 * answer on `<body data-sb-bottom>` for the stylesheet, and if a page ever renders
 * both it hides the tabs rather than letting them overlap the total the customer is
 * about to pay.
 */
function bottomBars() {
	const bar = one("[data-sb-bar]");
	document.body.setAttribute("data-sb-bottom", bar ? "bar" : "tabs");
	if (!bar) return;
	all(".sb-tabbar").forEach(function (tabs) {
		tabs.setAttribute("hidden", "hidden");
	});
}

/* ── the bag ───────────────────────────────────────────────────────────────── */

/** Paint the count on every badge, or hide them all. */
function paintBadge() {
	const stored = bagStore.get();
	const count = stored ? stored.count : 0;
	all(BADGE).forEach(function (badge) {
		if (count > 0) {
			badge.textContent = String(count);
			badge.removeAttribute("hidden");
		} else {
			// Hidden rather than zeroed: a stale "0" beside a bag icon reads as an
			// empty bag, and this browser may simply not be able to remember.
			badge.setAttribute("hidden", "hidden");
			badge.textContent = "0";
		}
	});
}

/**
 * Point every "Bag" link at the bag this browser is actually carrying.
 *
 * The header, the sheet and the tab bar all print a bare `/bag`, because the server
 * has no way to know what is in it. Without this, a parent who added a book on the
 * category page would tap Bag and find it empty.
 */
function paintBagLinks() {
	// On the bag itself the address bar is already the bag; rewriting these would
	// point the page at a copy of itself.
	if (window.location.pathname === "/bag") return;

	const stored = bagStore.get();

	// EXACTLY the shell's own bare `/bag` links — the header pill, the phone tab bar,
	// the sheet. Matching `a[href^="/bag"]` instead would also catch the set page's
	// sticky bar, whose href is the list the customer is looking at RIGHT NOW; that
	// button must go where its own page says, not where an older bag says. Already
	// rewritten links no longer match the bare href, which is what the marker is for.
	all('a[href="/bag"], a[data-sb-bag-link]').forEach(function (link) {
		// The server's own plain `/bag` is kept, so a bag that is emptied — an order
		// placed, a list cleared — puts every link back rather than leaving them
		// pointing at books that are gone.
		if (!link.hasAttribute("data-sb-bag-link")) {
			link.setAttribute("data-sb-bag-link", link.getAttribute("href"));
		}
		link.setAttribute("href", stored ? stored.href : link.getAttribute("data-sb-bag-link"));
	});
}

/**
 * Learn the bag from the page the customer is standing on.
 *
 * On `/bag` the server has just re-priced the whole thing, so its own URL and its own
 * rows are the best record there will ever be — better than anything the browser
 * accumulated, which is why this OVERWRITES rather than merges. On
 * `/order-placed/...` the bag has become an order and must be forgotten, or the
 * badge would follow the customer around with books they have already paid for.
 */
function syncBagFromPage() {
	const path = window.location.pathname;

	if (path.indexOf("/order-placed") === 0) {
		bagStore.clear();
		return;
	}

	if (path !== "/bag") return;

	const card = one("[data-sb-bag-card]");
	if (!card) return;

	// Counted the way `set.js` counts, so the badge does not change its mind between
	// the list and the bag: rows the customer removed are still drawn (struck
	// through, so they can put them back) and are not in the bag, and pending rows
	// are on the list but are NOT being handed over or charged for today
	// (BUILD_DECISIONS section 6). The badge is what you are collecting.
	const rows = all("[data-sb-row]", card).filter(function (row) {
		const state = row.getAttribute("data-sb-state");
		return state !== "removed" && state !== "pending";
	});

	if (!rows.length) {
		bagStore.clear();
		return;
	}

	const checkout = one("[data-sb-checkout]");
	const loose = [];
	if (checkout) {
		all('input[name="loose"]', checkout).forEach(function (input) {
			const parts = String(input.value || "").split(":");
			if (parts[0]) loose.push({ item_code: parts[0], qty: parseInt(parts[1], 10) || 1 });
		});
	}

	bagStore.set({
		href: window.location.pathname + window.location.search,
		count: rows.length,
		// Left at zero deliberately. The bag page prints its own total from the
		// server's quote, nothing reads this field, and a figure scraped back out of
		// formatted text would be a second opinion about money — which is the one
		// thing this storefront is built not to have (BUILD_DECISIONS section 4).
		total: 0,
		loose: loose
	});
}

/**
 * "Add to bag", from a product card's `+` or the book page's button.
 *
 * The control is a real link or a real submit first (`macros.product_card` and
 * `splashbox-book.html` both say so), so this only has to beat what the browser
 * would do anyway. It never asks the server: adding an item is a change to a URL,
 * and the price of that item is re-read by `pricing.build_quote` on the bag page and
 * again by `create_order` at checkout (BUILD_DECISIONS section 11).
 */
function addToBag() {
	document.addEventListener("click", function (event) {
		const control = event.target.closest("[data-sb-add]");
		if (!control) return;

		const code = control.getAttribute("data-sb-add");
		if (!code) return;

		event.preventDefault();

		let qty = 1;
		const form = control.form || control.closest("form");
		const field = form ? one('input[name="qty"]', form) : null;
		if (field) qty = Math.max(1, Math.min(999, parseInt(field.value, 10) || 1));

		// Whatever the bag already held that is NOT a loose row — a school set, put
		// there by `set.js` with the server's own billable count — is carried
		// through. Recounting from the URL instead would drop a twelve-book set to
		// "1" the moment a parent added a pen to it.
		const before = bagStore.get();
		const setPortion = before ? Math.max(0, before.count - before.loose.length) : 0;

		const href = mergeLoose(code, qty);
		const loose = readLoose(href);
		bagStore.set({
			href: href,
			count: setPortion + loose.length,
			total: before ? before.total : 0,
			loose: loose
		});
		paintBadge();
		paintBagLinks();
		flash(control);

		// On the bag itself there is nothing to flash — the page has to re-price.
		if (window.location.pathname === "/bag") window.location.assign(href);
	});
}

/** The bag's query string, as a `URLSearchParams` we can edit. */
function bagParams() {
	const stored = bagStore.get();
	const href = stored ? stored.href : "/bag";
	const cut = href.indexOf("?");
	return new URLSearchParams(cut === -1 ? "" : href.slice(cut + 1));
}

/** `[{item_code, qty}]` out of a `/bag?...&loose=CODE:QTY` href. */
function readLoose(href) {
	const cut = href.indexOf("?");
	const params = new URLSearchParams(cut === -1 ? "" : href.slice(cut + 1));
	const rows = [];
	params.getAll("loose").forEach(function (value) {
		const parts = String(value).split(":");
		if (parts[0]) rows.push({ item_code: parts[0], qty: parseInt(parts[1], 10) || 1 });
	});
	return rows;
}

/** Add one item to the bag's `loose` list, or bump the quantity it already has. */
function mergeLoose(code, qty) {
	const params = bagParams();
	const rows = [];
	let found = false;

	params.getAll("loose").forEach(function (value) {
		const parts = String(value).split(":");
		if (!parts[0]) return;
		if (parts[0] === code) {
			found = true;
			rows.push(code + ":" + Math.min(999, (parseInt(parts[1], 10) || 1) + qty));
		} else {
			rows.push(parts[0] + ":" + (parseInt(parts[1], 10) || 1));
		}
	});

	if (!found) rows.push(code + ":" + qty);

	params.delete("loose");
	rows.slice(0, 50).forEach(function (row) {
		params.append("loose", row);
	});

	const query = params.toString();
	return "/bag" + (query ? "?" + query : "");
}

/**
 * A moment of "yes, that worked" on the control the customer just pressed.
 *
 * The children are stashed on a detached clone rather than the text being read and
 * written back: `macros.product_card`'s `+` is text, but the book page's button is
 * an icon followed by a word, and restoring that with `textContent` would delete the
 * icon for good.
 */
function flash(control) {
	if (control.getAttribute("data-sb-flashing")) return;

	const original = control.cloneNode(true);
	control.setAttribute("data-sb-flashing", "1");
	control.classList.add("sb-added");
	empty(control);
	control.appendChild(
		document.createTextNode(control.classList.contains("sb-add") ? "✓" : "Added to bag")
	);

	setTimeout(function () {
		empty(control);
		while (original.firstChild) control.appendChild(original.firstChild);
		control.classList.remove("sb-added");
		control.removeAttribute("data-sb-flashing");
	}, 1200);
}

/* ── log out ───────────────────────────────────────────────────────────────── */

/**
 * The header's "Log out" is an `<a href="#">`, which is a dead control without this.
 *
 * The page-level log out (`account/login.html`) is a real form POST to the same
 * endpoint and is deliberately left alone here — that is the copy of this control
 * that works with the script blocked, and a shared phone at a shop counter is
 * exactly where someone needs it.
 */
function logout() {
	all("a[data-sb-logout]").forEach(function (link) {
		link.addEventListener("click", function (event) {
			event.preventDefault();
			post(METHODS.logout, {})
				.then(function (data) {
					// A session that ended must not leave a bag or a remembered list
					// behind for whoever picks the phone up next.
					bagStore.clear();
					lastList.clear();
					window.location.assign(safePath(data && data.redirect_to) || "/");
				})
				.catch(function (error) {
					if (!(error instanceof ApiError)) throw error;
					// Still signed in. `/account/login` renders the "Signed in as …"
					// panel with a real log-out form on it, which is the honest place
					// to send someone whose log out did not go through.
					window.location.assign("/account/login");
				});
		});
	});
}

/* ── the list this browser last opened ─────────────────────────────────────── */

/**
 * Rewrite the home page's "last list" row from memory.
 *
 * The server prints this row pointing at the featured school's full class grid,
 * because that is the honest thing it can know about a visitor it has never met.
 * When this browser remembers a set, the row becomes that set instead — same shape,
 * same tap target, no layout shift.
 */
function paintLastList() {
	const row = one("[data-sb-last-list]");
	if (!row) return;

	const entry = lastList.get();
	if (!entry || !entry.title) return;

	row.setAttribute("href", entry.href);

	const text = one(".sb-txt", row);
	if (text) {
		while (text.firstChild) text.removeChild(text.firstChild);
		const micro = document.createElement("span");
		micro.className = "sb-micro";
		micro.textContent = "Your last list";
		text.appendChild(micro);
		text.appendChild(document.createElement("br"));
		text.appendChild(document.createTextNode(entry.title));
	}

	const meta = one(".sb-meta", row);
	if (meta) meta.textContent = entry.meta || entry.code || "Open it again";
}

/* ── filtering a list that is already on the page ──────────────────────────── */

/**
 * Type-to-filter over rows the server already rendered.
 *
 * Both places this runs — the home finder panel and `/schools` — also have a real
 * GET form behind them that searches the whole catalogue server-side, and pressing
 * Enter still does exactly that. This only narrows what is already visible, which is
 * why it costs no request and works with the connection gone.
 */
function filterList(input, list) {
	if (!input || !list) return;

	const rows = all(".sb-row-link", list);
	if (!rows.length) return;

	let note = null;

	function ensureNote() {
		if (note) return note;
		note = document.createElement("p");
		note.className = "sb-cap sb-pad";
		note.textContent = "No school on this page matches that — press Enter to search them all.";
		note.setAttribute("hidden", "hidden");
		list.appendChild(note);
		return note;
	}

	function apply() {
		const query = needle(input.value);
		let shown = 0;

		rows.forEach(function (row) {
			if (!query) {
				row.removeAttribute("hidden");
				shown += 1;
				return;
			}
			const name = needle(row.textContent);
			if (name.indexOf(query) !== -1) {
				row.removeAttribute("hidden");
				shown += 1;
			} else {
				row.setAttribute("hidden", "hidden");
			}
		});

		const emptyNote = ensureNote();
		if (shown === 0) emptyNote.removeAttribute("hidden");
		else emptyNote.setAttribute("hidden", "hidden");
	}

	input.addEventListener("input", apply);
	// A `type="search"` field fires `search` on the little clear cross, and older
	// WebKit does not fire `input` for it.
	input.addEventListener("search", apply);
}

function filters() {
	filterList(one("[data-sb-school-filter]"), one("[data-sb-school-list]"));
	filterList(one("[data-sb-finder]"), one("[data-sb-typeahead]"));
}

/* ── theme ─────────────────────────────────────────────────────────────────── */

/**
 * Re-apply a theme this customer chose on an earlier visit.
 *
 * `public/scss/_tokens.scss` builds the palette so that an explicit choice is
 * `:root[data-theme="dark"|"light"]` and the absence of the attribute means "follow
 * the device". Storage is written the same way round, so `system` is simply not
 * setting anything.
 *
 * There is no theme control in the shipped templates yet; `[data-sb-theme]` is bound
 * here so that adding one is a template change and not a bundle change.
 */
function themes() {
	const chosen = themeStore.get();
	const root = document.documentElement;
	if (chosen === "dark" || chosen === "light") root.setAttribute("data-theme", chosen);

	all("[data-sb-theme]").forEach(function (control) {
		control.addEventListener("click", function (event) {
			event.preventDefault();
			const order = ["system", "light", "dark"];
			const next = order[(order.indexOf(themeStore.get()) + 1) % order.length];
			themeStore.set(next);
			if (next === "system") root.removeAttribute("data-theme");
			else root.setAttribute("data-theme", next);
			control.setAttribute("data-sb-theme", next);
		});
	});
}

/* ── boot ──────────────────────────────────────────────────────────────────── */

/**
 * Wire the shell. Each piece is independent and each one is a no-op on a page that
 * does not have its markup, so this runs unconditionally on every storefront page.
 */
export function init() {
	themes();
	menus();
	sheet();
	bottomBars();
	syncBagFromPage();
	paintBadge();
	paintBagLinks();
	addToBag();
	logout();
	paintLastList();
	filters();
}

/** Let the other modules refresh the badge after they have changed the bag. */
export { paintBadge, paintBagLinks };
